import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Icon } from '@nazka/nazka.mapframe.components';

import theme from '@style/theme.json';
import { useTranslation } from '@utils/i18n/i18n';

import { INDICATORS, MONTH_NAMES } from '../dashboard.constants';

import './indicators-graph.css';

function IndicatorsGraphTooltip({ active, payload, label }) {
  if (!active || !payload?.length) {
    return null;
  }

  const hoveredPoint = payload[0]?.payload;

  return (
    <div className="indicators-graph__tooltip">
      <strong>{`Month ${label}`}</strong>
      <span>{`${MONTH_NAMES[hoveredPoint?.month]} ${hoveredPoint?.year}`}</span>
      <div className="indicators-graph__tooltip__values">
        {payload.map(({ dataKey, value, color }) => (
          <div key={dataKey} className="indicators-graph__tooltip__item">
            <span
              className="indicators-graph__tooltip__dot"
              style={{ backgroundColor: color }}
            />
            <span>{INDICATORS[dataKey].label}</span>
            <strong>{Number(value).toFixed(1)}</strong>
          </div>
        ))}
      </div>
    </div>
  );
}

function renderSelectedDot(props) {
  const {
    cx,
    cy,
    stroke,
    payload,
    selectedIndex,
  } = props;

  if (payload?.index !== selectedIndex) {
    return null;
  }

  return (
    <g>
      <circle cx={cx} cy={cy} r={9} fill={stroke} fillOpacity={0.2} />
      <circle cx={cx} cy={cy} r={5} fill={stroke} stroke="#fff" strokeWidth={2} />
    </g>
  );
}

function IndicatorsGraph({
  data,
  selectedDataPoint,
}) {
  const { t } = useTranslation();

  return (
    <div className="indicators-graph">
      <div className="indicators-graph__header">
        <h3>{t('Indicators over time')}</h3>
      </div>
      <div className="indicators-graph__wrapper">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 16, right: 24, bottom: 24, left: 8 }}>
            <CartesianGrid stroke={theme.color['chart-grid']} strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="index"
              tickLine={false}
              axisLine={{ stroke: theme.color.border }}
              label={{ value: t('Months'), position: 'insideBottom', offset: -10 }}
            />
            <YAxis
              domain={[0, 100]}
              tickLine={false}
              axisLine={{ stroke: theme.color.border }}
              label={{ value: t('Indicator value'), angle: -90, position: 'insideLeft' }}
            />
            <Tooltip content={<IndicatorsGraphTooltip />} />
            {selectedDataPoint && (
              <ReferenceLine
                x={selectedDataPoint.index}
                stroke="#7C8B96"
                strokeDasharray="4 4"
                ifOverflow="extendDomain"
              />
            )}
            {Object.entries(INDICATORS).map(([key, { color }]) => (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                stroke={color}
                strokeWidth={3}
                dot={props => renderSelectedDot({ ...props, selectedIndex: selectedDataPoint?.index })}
                activeDot={{ r: 6, stroke: '#fff', strokeWidth: 2 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="indicators-graph__legend">
        {Object.entries(INDICATORS).map(([key, { label, color, icon }]) => (
          <div key={key} className="indicators-graph__legend__item">
            <Icon
              className="indicators-graph__legend__icon"
              css={{ backgroundColor: color }}
              color="white"
              icon={icon}
            />
            <span>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default IndicatorsGraph;
